import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { FloraNewChatReason } from '../flora/flora.type';
import { useFloraChatSession } from '../flora/hooks/use-flora-chat-session';
import { isFloraSettingsPath } from './components/agent-settings/agent-settings.constants';

/**
 * Full-page Flora chat: the shared chat session wired to the flora-agent routes.
 * The URL's `chatId` selects the thread, New Chat returns to the base route, and
 * a new conversation catches the URL up to its thread. Only this surface resumes
 * the job stashed in localStorage on refresh (the side panel resumes through the
 * chat's `active_job_id`).
 *
 * @returns The chat state and actions consumed by `FloraAgentLayout`
 */
function useFloraAgent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams<{ chatId: string }>();

  // Only redirect from the base chat route, not sub-routes like /documents or /preferences
  const isOnChatRoute =
    location.pathname === '/application/flora-agent' || location.pathname === '/application/flora-agent/';

  // An org/portfolio switch resets the chat in the background; on a settings
  // page that must not throw the user back to the chat. Settings re-fetch per
  // organization, so staying put shows the new organization's values.
  const isOnSettingsRoute = isFloraSettingsPath(location.pathname);
  const handleNavigateToNewChat = useCallback(
    (reason: FloraNewChatReason) => {
      if (reason === FloraNewChatReason.ContextSwitch && isOnSettingsRoute) return;
      navigate('/application/flora-agent');
    },
    [isOnSettingsRoute, navigate],
  );

  const handleConversationStarted = useCallback(
    (threadId: string) => {
      if (isOnChatRoute) {
        navigate(`/application/flora-agent/${threadId}`, { replace: true });
      }
    },
    [isOnChatRoute, navigate],
  );

  const session = useFloraChatSession({
    chatId,
    isSidebar: false,
    resumeStoredJob: true,
    onNewChat: handleNavigateToNewChat,
    onConversationStarted: handleConversationStarted,
  });

  const handleHistoryClick = useCallback(
    (id: string) => {
      navigate(`/application/flora-agent/${id}`);
    },
    [navigate],
  );

  return {
    messages: session.messages,
    questions: session.questions,
    threadId: session.threadId,
    chatId,
    chatHistories: session.chatHistories,
    question: session.question,
    isActiveQuestion: session.isActiveQuestion,
    isWaitingForFirstResponse: session.isWaitingForFirstResponse,
    loadingMessage: session.loadingMessage,
    tokenUsage: session.tokenUsage,
    latestTurnIsLive: session.latestTurnIsLive,
    useAI: session.useAI,
    floraVersion: session.floraVersion,
    submitQuestion: session.submitQuestion,
    attachments: session.attachments,
    setQuestion: session.setQuestion,
    handleNewChat: session.handleNewChat,
    handleDeleteHistory: session.handleDeleteHistory,
    handleEditHistory: session.handleEditHistory,
    handleHistoryClick,
    handleAnswerSubmit: session.handleAnswerSubmit,
    stopAgent: session.stopAgent,
    handleToolApproval: session.handleToolApproval,
    fetchChatHistories: session.fetchChatHistories,
  };
}

function useFloraDarkMode() {
  const STORAGE_KEY = 'flora-agent-dark-mode';

  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  }, []);

  return { isDarkMode, toggleDarkMode };
}

/**
 * Auto-opens a right-rail panel exactly when its content is produced during the
 * live turn — never when merely loading a conversation that already contains
 * that content.
 *
 * Responsibilities:
 * 1. Detect a fresh item added this render (count grew since the last render)
 * 2. Invoke `onShouldOpen` only when that happens AND the latest turn is live,
 *    so `onShouldOpen` can preserve a panel the user already opened
 * 3. Rebaseline (without opening) when the conversation changes, so the count
 *    baseline never leaks across conversations — a new conversation starting
 *    with a lower count could otherwise read its first item as a removal, and
 *    a conversation opened at a non-zero count would look like a live add
 *
 * @param {number} count - Number of eligible items in the current conversation
 * @param {string} conversationKey - Stable id for the active conversation/thread
 * @param {boolean} latestTurnIsLive - True only for a turn that streamed in this session
 * @param {() => void} onShouldOpen - Stable callback that opens the panel (should itself no-op if another panel is already open)
 * @returns {void}
 */
function useRailAutoOpenOnLivePublish(
  count: number,
  conversationKey: string,
  latestTurnIsLive: boolean,
  onShouldOpen: () => void,
): void {
  const previousCountRef = useRef(0);
  const conversationKeyRef = useRef(conversationKey);

  useEffect(() => {
    if (conversationKeyRef.current !== conversationKey) {
      conversationKeyRef.current = conversationKey;
      previousCountRef.current = count;
      return;
    }

    const itemAdded = count > previousCountRef.current;
    previousCountRef.current = count;

    if (itemAdded && latestTurnIsLive) {
      onShouldOpen();
    }
  }, [count, conversationKey, latestTurnIsLive, onShouldOpen]);
}

export { useFloraAgent, useFloraDarkMode, useRailAutoOpenOnLivePublish };
